import { Injectable, inject, Injector } from '@angular/core';
import { FrontendApi, Session } from '@ory/client';
import { environment } from '@storees/environments';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { AxiosResponse } from 'axios';
import { ORY_CONFIG_TOKEN } from './ory-flow.service';
import { Constants } from '../utils/Constants';
import { SessionService } from './session.service';
import { StoreesBackendService } from './storees-backend.service';
import { DexieDbService } from './dexie/dexie-db-service';
import {
  ACCOUNT_SETTINGS,
  NULL_UUID,
  SIGN_IN,
  SIGN_UP,
  VOICE_CLONE_RETURN_ROUTE,
  VOICE_CLONE_SHOW_SUBSCRIBE_NOW,
} from '@storees/shared-constants';
import { Qonversion, UserPropertyKey } from '@qonversion/capacitor-plugin';
import { LoadingService } from './loading.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertController } from '@ionic/angular/standalone';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { logBillingError } from '../utils/billing-error-logger';
import { ProfileService } from './profile-service';
import { DexieNarratorsService } from './dexie/dexie-narrators.service';
import { DexieProfilesService } from './dexie/dexie-profiles.service';
import { DexieTrophiesService } from './dexie/dexie-trophies-service';
import { ProfileCreationStateService } from './profile-creation-state.service';
import { StoryCreationStateService } from './story-creation-state.service';
import { VoiceCloneService } from './voice-clone.service';
import { PushNotificationService } from './push-notification.service';
import {
  AsyncTimeoutError,
  ORY_TOKENIZE_SESSION_TIMEOUT_MS,
  ORY_TO_SESSION_RETRY_POLL_TIMEOUT_MS,
  ORY_TO_SESSION_TIMEOUT_MS,
  promiseWithTimeout,
  QONVERSION_IDENTIFY_TIMEOUT_MS,
} from '../utils/async-timeout';

const {
  storeesJwtTemplate,
  googleWebClientId,
  googleIosClientId,
  googleRedirectUrl,
  oryGoogleWebOidcRedirectPathPart,
  oryGoogleIosOidcRedirectPathPart,
  oryFacebookOidcRedirectPathPart,
  oryAppleOidcRedirectPathPart
} = environment;

type SocialAuthResult = {
  usedExistingAccount: boolean;
};

const ADD_NEW_FROM_CHARACTER = 'ADD_NEW_FROM_CHARACTER';
const LAST_CREATED_PROFILE_ID = 'LAST_CREATED_PROFILE_ID';
const PENDING_TROPHY_UNLOCKS_KEY = 'pending_trophy_unlocks';
const PROFILE_EDIT_ORIGINAL_BIRTHDATE_KEY = 'profile_edit_original_birthdate';
const PROFILE_EDIT_ORIGINAL_NARRATOR_KEY = 'profile_edit_original_narrator';
const PROFILE_EDIT_ORIGINAL_IMAGE_STYLE_KEY =
  'profile_edit_original_image_style';
const PROFILE_EDIT_ORIGINAL_PROFILE_IMAGE_KEY =
  'profile_edit_original_profile_image';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router = inject(Router);
  private session = inject(SessionService);
  private storeesBackendService = inject(StoreesBackendService);
  private dexieDb = inject(DexieDbService);
  translate = inject(TranslateService);
  private alertCtrl = inject(AlertController);
  private loadingService = inject(LoadingService);
  private injector = inject(Injector);

  private frontendApi: FrontendApi;
  private lastAnonymousTokenCheckTime = 0;

  /**
   * Serializes JWT refresh so concurrent callers (account-settings refresh, HTTP interceptor recovery,
   * login success) cannot interleave clears/whoami/native token fetches — a common cause of flaky
   * second-login hangs on Capacitor.
   */
  private sessionJwtMutationChain: Promise<void> = Promise.resolve();

  /**
   * Serializes Qonversion identity updates so `identify()` / `setUserProperty` are not interleaved
   * with a concurrent call from account-settings right after navigation.
   */
  private qonversionPurchaseConfigChain: Promise<void> = Promise.resolve();

  constructor() {
    const frontendApi = inject<FrontendApi>(ORY_CONFIG_TOKEN);

    this.frontendApi = frontendApi;
  }

  async startAuthCheck(navigateToSignIn = false) {
    const isLoggedIn = await this.isUserLoggedIn();
    if (isLoggedIn) {
      await this.router.navigate([`${ACCOUNT_SETTINGS}`]);
    } else if (navigateToSignIn) {
      await this.router.navigate([`${SIGN_IN}`]);
    } else {
      await this.router.navigate([`${SIGN_UP}`]);
    }
  }

  async getOrySession(options?: { timeoutMs?: number }) {
    const timeoutMs =
      options?.timeoutMs !== undefined ? options.timeoutMs : ORY_TO_SESSION_TIMEOUT_MS;

    let session: AxiosResponse<Session, unknown> | null = null;
    try {
      session = (await promiseWithTimeout(
        this.frontendApi.toSession(),
        timeoutMs,
        'Ory toSession(whoami)'
      )) as AxiosResponse<Session>;
    } catch (err: unknown) {
      if (err instanceof AsyncTimeoutError) {
        console.error('Error checking session (timed out):', err.message);
        return session;
      }
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 401 || axiosErr.response?.status === 403) {
        console.log('User is not logged in');
      }
      console.error('Error checking session:', err);
    }
    return session;
  }

  async isUserLoggedIn(options?: { whoamiTimeoutMs?: number }): Promise<boolean> {
    let isLoggedIn;
    const timeoutOpts =
      options?.whoamiTimeoutMs !== undefined
        ? { timeoutMs: options.whoamiTimeoutMs }
        : undefined;
    const session = await this.getOrySession(timeoutOpts);

    if (session?.data) {
      // Check if session has valid data structure
      const sessionData = session.data;

      // Validate session has required fields
      if (sessionData.id && sessionData.identity) {
        // Check if session is active (not expired)
        if (sessionData.active === true) {
          console.log('User has valid active session:', sessionData.id);
          isLoggedIn = true;
        } else if (sessionData.expires_at) {
          // If no active flag, check expiration date
          const expirationDate = new Date(sessionData.expires_at);
          const now = new Date();

          if (expirationDate > now) {
            console.log('User has valid non-expired session:', sessionData.id);
            isLoggedIn = true;
          } else {
            console.log('Session has expired:', sessionData.expires_at);
            isLoggedIn = false;
          }
        } else {
          // If no active flag and no expiration, assume valid for backward compatibility
          console.log('User has session with no expiration info, assuming valid:', sessionData.id);
          isLoggedIn = true;
        }
      } else {
        console.log('Session exists but missing required fields (id or identity)');
        isLoggedIn = false;
      }
    } else {
      console.log('Session is null or has no data');
      isLoggedIn = false;
    }

    return isLoggedIn;
  }

  /**
   * Resolves the user's Ory identity id (Kratos identity primary key) when signed in.
   *
   * Returns null for anonymous sessions. Anonymous users intentionally remain unidentified in
   * Qonversion so that {@link configurePurchasesWithAppUserId} can hand the device's
   * still-unidentified QUID — and any anonymous purchase recorded against it — to Qonversion's
   * `identify()` on first sign-in. Qonversion only merges the QUID's purchases into the new
   * user id when the QUID is currently unidentified; once identified (e.g. with our
   * `anonymousId`), a later `identify(orySessionId)` would just *switch* identity and the
   * anonymous purchase would be stranded on the prior identifier.
   */
  private async getOrySessionIdentityId(): Promise<string | null> {
    const orySession = await this.getOrySession();
    const sessionIdentityId = orySession?.data?.identity?.id;
    if (typeof sessionIdentityId === 'string' && sessionIdentityId.trim().length > 0) {
      return sessionIdentityId.trim();
    }
    return null;
  }

  /**
   * Reads the `anonymousId` claim from the cached anonymous JWT (issued by
   * `/v1/get-anonymous-token`). Used as Qonversion's `CUSTOM_USER_ID` so the
   * Qonversion → backend webhook still carries our `UserIdentity.providerUserId`
   * for anonymous users (since we no longer call `identify(anonymousId)`).
   */
  private async getAnonymousJwtAnonymousId(): Promise<string | null> {
    const jwtToken = await this.getSessionJwtToken();
    if (!jwtToken) {
      return null;
    }
    const payload = this.parseJwtPayload(jwtToken);
    if (!payload) {
      return null;
    }
    const anonymousId = payload['anonymousId'];
    return typeof anonymousId === 'string' && anonymousId.length > 0
      ? anonymousId
      : null;
  }

  /**
   * Wires the current session into Qonversion so entitlement checks (and the resulting
   * webhook traffic) target the correct user.
   *
   * - Signed in: call `Qonversion.identify(orySessionIdentityId)`. If the device's QUID
   *   was previously anonymous and recorded a purchase, Qonversion's server merges that
   *   purchase into this Ory identity. This is the single moment that links an
   *   anonymous-mode purchase to the user's account.
   * - Anonymous: deliberately do NOT call `identify`. Identifying with our `anonymousId`
   *   would lock the QUID to that id, and a later `identify(orySessionId)` would just
   *   switch identity (no merge), leaving the purchase stranded on `anonymousId`.
   *   We instead surface our `anonymousId` to Qonversion as `CUSTOM_USER_ID` so trial
   *   webhooks can still resolve the user via `UserIdentity.providerUserId`.
   *
   * On sign-out we call {@link signOutFromQonversion} so the next anonymous flow gets a
   * fresh QUID instead of inheriting the previous user's Qonversion identity.
   */
  async configurePurchasesWithAppUserId() {
    const next = this.qonversionPurchaseConfigChain.then(() =>
      this.performConfigurePurchasesWithAppUserId()
    );
    this.qonversionPurchaseConfigChain = next.then(
      () => undefined,
      () => undefined
    );
    await next;
  }

  private async performConfigurePurchasesWithAppUserId(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const orySessionIdentityId = await this.getOrySessionIdentityId();

    if (orySessionIdentityId) {
      console.log(
        'Qonversion identify (Ory identity id — matches UserIdentity.providerUserId / webhooks identity_id):',
        orySessionIdentityId
      );

      try {
        const userInfo = await promiseWithTimeout(
          Qonversion.getSharedInstance().identify(orySessionIdentityId),
          QONVERSION_IDENTIFY_TIMEOUT_MS,
          'Qonversion.identify'
        );
        console.log('Qonversion user info: ', userInfo);
      } catch (e: unknown) {
        if (e instanceof AsyncTimeoutError) {
          logBillingError('AuthService - Qonversion identify (timeout)', e);
        } else {
          logBillingError('AuthService - Qonversion identify', e);

          const err = e as { message?: string };
          if (
            err?.message?.includes('ProxyBillingActivity') ||
            err?.message?.includes('PendingIntent')
          ) {
            await this.showBillingErrorAlert();
          }
        }
      }
      return;
    }

    // Anonymous: keep Qonversion unidentified so a future identify() can merge the QUID's
    // purchase into the user's Ory identity. Surface our anonymousId via CUSTOM_USER_ID
    // so webhooks (e.g. trial reminders) can still match the same UserIdentity row.
    const anonymousId = await this.getAnonymousJwtAnonymousId();
    if (!anonymousId) {
      return;
    }

    try {
      Qonversion.getSharedInstance().setUserProperty(
        UserPropertyKey.CUSTOM_USER_ID,
        anonymousId
      );
      console.log(
        'Qonversion CUSTOM_USER_ID set for anonymous session (no identify):',
        anonymousId
      );
    } catch (e: unknown) {
      logBillingError(
        'AuthService - Qonversion setUserProperty(CUSTOM_USER_ID)',
        e
      );
    }
  }

  /**
   * Links the current device's Qonversion user to our backend-resolvable identity
   * immediately after a successful purchase. Anonymous sessions normally avoid
   * {@link configurePurchasesWithAppUserId}'s `identify()` so a later Ory sign-in
   * can merge purchases; post-purchase we still need the backend to resolve Bronze
   * via `/v3/identities/{anonymousId}` for voice-clone story generation.
   */
  async syncQonversionIdentityAfterPurchase(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const orySessionIdentityId = await this.getOrySessionIdentityId();
    const identityToLink =
      orySessionIdentityId ?? (await this.getAnonymousJwtAnonymousId());
    if (!identityToLink) {
      return;
    }

    try {
      await promiseWithTimeout(
        Qonversion.getSharedInstance().identify(identityToLink),
        QONVERSION_IDENTIFY_TIMEOUT_MS,
        'Qonversion.identify (post-purchase)'
      );
    } catch (e: unknown) {
      logBillingError('AuthService - Qonversion identify after purchase', e);
    }
  }

  /**
   * Resets the device's Qonversion identity back to a fresh anonymous QUID. Call this
   * whenever the local Ory session goes away (sign out, delete account) so the next
   * anonymous session — and any purchase it makes — is not silently attached to the
   * previous user's Qonversion identity.
   */
  async signOutFromQonversion(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      Qonversion.getSharedInstance().logout();
    } catch (e: unknown) {
      logBillingError('AuthService - Qonversion logout', e);
    }
  }

  private parseJwtPayload(jwtToken: string): Record<string, unknown> | null {
    try {
      const payloadBase64 = jwtToken.split('.')[1];
      if (!payloadBase64) {
        return null;
      }
      return JSON.parse(atob(payloadBase64)) as Record<string, unknown>;
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  private async showBillingErrorAlert() {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('Purchase System Unavailable'),
      message: this.translate.instant('The purchase system is temporarily unavailable. Please ensure Google Play Services is up to date and try again.'),
      buttons: [this.translate.instant('general.ok')]
    });
    await alert.present();
  }

  async getOrySessionEmail(): Promise<string> {
    let email = '';
    const orySession = await this.getOrySession();
    if (orySession) {
      console.log('Ory Session: ', orySession);
      console.log(
        'Ory identity trait email: ',
        orySession?.data.identity?.traits?.email
      );
      email = orySession?.data?.identity?.traits?.email;
    }
    return email;
  }

  async getUserId(): Promise<string | undefined> {
    try {
      const orySession = await this.getOrySession();
      if (orySession?.data?.identity?.id) {
        return orySession.data.identity.id;
      }
      return undefined;
    } catch (error) {
      console.error('Error getting user ID from session:', error);
      return undefined;
    }
  }

  public async getOrySessionJWT() {
    try {
      const session = await promiseWithTimeout(
        this.frontendApi.toSession({
          tokenizeAs: storeesJwtTemplate,
        }),
        ORY_TOKENIZE_SESSION_TIMEOUT_MS,
        'Ory toSession(JWT)'
      );
      if (session?.data?.tokenized) {
        return session.data.tokenized;
      }
    } catch (err: unknown) {
      if (err instanceof AsyncTimeoutError) {
        console.error('Error getting session while tokenizing JWT (timed out):', err.message);
      } else {
        console.error('Error getting session while doing session to JWT: ', err);
      }
    }

    return null;
  }

  async upgradeUser() {
    const jwtToken = await this.getSessionJwtToken();
    const oryToken = await this.getOrySessionJWT();
    if (jwtToken && oryToken) {
      await this.storeesBackendService.upgradeUser(jwtToken, oryToken);
      await this.setSessionJwtToken(oryToken);
      await this.syncPushTokenForCurrentSession();
    }
  }

  /**
   * @param options.retryNativeOryWhoami When true (e.g. right after native Ory login), retry
   *   `/sessions/whoami` briefly: the session cookie can lag behind Dexie's anonymous JWT, and
   *   a single failing whoami would otherwise skip Ory tokenization and leave the user looking
   *   "stuck" on anonymous.
   */
  public async updateSessionJWT(options?: {
    retryNativeOryWhoami?: boolean;
  }): Promise<void> {
    const next = this.sessionJwtMutationChain.then(() =>
      this.performUpdateSessionJWT(options ?? {})
    );
    this.sessionJwtMutationChain = next.then(
      () => undefined,
      () => undefined
    );
    await next;
  }

  /** Core JWT sync; must only run serialized — use {@link updateSessionJWT}. */
  private async performUpdateSessionJWT(options: {
    retryNativeOryWhoami?: boolean;
  }): Promise<void> {
    let existingJwtToken = await this.getSessionJwtToken();
    let loggedIn = await this.isUserLoggedIn();

    if (
      options?.retryNativeOryWhoami &&
      Capacitor.isNativePlatform() &&
      !loggedIn &&
      !!existingJwtToken &&
      this.isAnonymousJwtPayload(existingJwtToken)
    ) {
      const maxAttempts = 8;
      const delayMs = 75;
      for (let i = 1; i < maxAttempts && !loggedIn; i++) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        loggedIn = await this.isUserLoggedIn({
          whoamiTimeoutMs: ORY_TO_SESSION_RETRY_POLL_TIMEOUT_MS,
        });
      }
    }

    if (loggedIn) {
      const oryToken = await this.getOrySessionJWT();
      if (oryToken) {
        await this.setSessionJwtToken(oryToken);
        existingJwtToken = oryToken;
      }
    }

    if (!existingJwtToken || this.isJWTExpired(existingJwtToken)) {
      const jwtToken = await this.storeesBackendService.getAnonymousToken(
        existingJwtToken
      );
      await this.setSessionJwtToken(jwtToken);
    }
  }

  async refreshSignedInVoiceCloneTrialState(): Promise<void> {
    const jwtToken = await this.getSessionJwtToken();
    if (!jwtToken) {
      return;
    }

    try {
      const voiceCloneService = this.injector.get(VoiceCloneService);
      await voiceCloneService.syncSignedInTrialState();
    } catch (error) {
      console.error('Failed to refresh signed-in voice clone trial state:', error);
    }
  }

  async completeExistingAccountLoginSuccess(
    navigateTo = ACCOUNT_SETTINGS
  ): Promise<void> {
    await this.handleExistingAccountSwitch();
    await this.refreshSignedInVoiceCloneTrialState();
    await this.router.navigate([navigateTo]);
    this.schedulePurchasesIdentifyAfterNavigation();
  }

  /**
   * Qonversion identify can block for a long time when Play Billing has an active subscription.
   * Run it after Account Settings is on-screen so sign-in / navigation never waits on billing.
   */
  private schedulePurchasesIdentifyAfterNavigation(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    void this.configurePurchasesWithAppUserId().catch((error: unknown) => {
      logBillingError(
        'AuthService - deferred Qonversion identify after navigation',
        error
      );
    });
  }

  /**
   * Returns the session JWT from Dexie first (persists across app upgrade/reinstall),
   * then falls back to Ionic Storage and migrates to Dexie if present.
   */
  public async getSessionJwtToken(): Promise<string> {
    const key = Constants.SESSION.JWT_TOKEN;
    const token = await this.dexieDb.getAppSessionValue(key);
    if (token !== undefined && token !== '') {
      return token;
    }
    const legacyToken = await this.session.getPersistent(key);
    if (legacyToken) {
      await this.dexieDb.setAppSessionValue(key, legacyToken);
      return typeof legacyToken === 'string' ? legacyToken : String(legacyToken);
    }
    return '';
  }

  public async setSessionJwtToken(jwtToken: string) {
    const key = Constants.SESSION.JWT_TOKEN;
    await this.dexieDb.setAppSessionValue(key, jwtToken);
    await this.session.setPersistent(key, jwtToken);
  }

  public async clearSessionJwtToken() {
    const key = Constants.SESSION.JWT_TOKEN;
    await this.dexieDb.clearAppSessionValue(key);
    await this.session.setPersistent(key, '');
  }

  public isJWTExpired(existingJWT: string) {
    let jwtIsExpired = false;

    if (!existingJWT) {
      return true;
    }

    try {
      // Decode JWT token to get the payload
      const payloadBase64 = existingJWT.split('.')[1];
      const payload = JSON.parse(atob(payloadBase64));

      // Check if the token has expired by comparing the exp timestamp (in seconds) with current time
      if (!payload.exp || payload.exp * 1000 <= Date.now()) {
        jwtIsExpired = true;
      }
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      jwtIsExpired = true;
    }

    return jwtIsExpired;
  }

  public async checkIfAnonymousToken(existingJwtToken: string, alertController: AlertController) {
    let isAnonymousOrExpired = false;
    // Guard: only execute once per hour (3600000 milliseconds)
    const currentTime = Date.now();
    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds

    if (currentTime - this.lastAnonymousTokenCheckTime < oneHour) {
      console.log('Skipping anonymous token check - executed within the last hour');
      return;
    }

    if (existingJwtToken) {
      if (this.isAnonymousJwtPayload(existingJwtToken) || this.isJWTExpired(existingJwtToken)) {
        isAnonymousOrExpired = true;
        console.log('AuthService - Existing JWT is anonymous or expired, starting anonymous check');
        // Show dialog for anonymous token
        const alert = await alertController.create({
          header: this.translate.instant(
            'general.alerts.sessionExpired.title'
          ),
          message: this.translate.instant(
            'general.alerts.sessionExpired.message'
          ),
          buttons: [
            {
              text: this.translate.instant('general.cancel'),
              role: 'cancel',
            },
            {
              text: this.translate.instant('general.ok'),
              handler: () => {
                this.startAuthCheck();
              },
            },
          ],
        });
        await alert.present();
      } else {
        if (this.isJWTExpired(existingJwtToken)) {
          isAnonymousOrExpired = true;
          // Show dialog for Ory token
          const alert = await alertController.create({
            header: this.translate.instant(
              'general.alerts.sessionExpired.title'
            ),
            message: this.translate.instant(
              'general.alerts.sessionExpired.message'
            ),
            buttons: [
              {
                text: this.translate.instant('general.cancel'),
                role: 'cancel',
              },
              {
                text: this.translate.instant('general.ok'),
                handler: () => {
                  this.router.navigate([SIGN_IN]);
                },
              },
            ],
          });
          await alert.present();
        }
      }

      // Update the last execution time after successful completion
      this.lastAnonymousTokenCheckTime = currentTime;
    }

    return isAnonymousOrExpired;
  }

  isAnonymousToken(jwtToken: string): boolean {
    const isAnonymous = this.isAnonymousJwtPayload(jwtToken);
    console.log('AuthService - isAnonymousToken: ', isAnonymous);
    return isAnonymous;
  }

  async handleError(error: any) {
    console.error('AuthService received error: ', error);

    // Check if this is a RequiredError with null/undefined flow parameter
    if (this.isFlowParameterError(error)) {
      await this.handleFlowParameterError(error);
      return;
    }

    // Check if this is a 400 error that might indicate an existing account
    if (this.isExistingAccountError(error)) {
      await this.handleExistingAccountError(error);
      return;
    }

    const alert = await this.alertCtrl.create({
      header: this.translate.instant(
        '@storees/ionic-angular.storees-auth.errors.accountError'
      ),
      message: error.message,
      buttons: [
        {
          text: this.translate.instant(`general.ok`),
        },
      ],
    });
    await alert.present();
  }

  private isFlowParameterError(error: any): boolean {
    // Check for RequiredError with flow parameter issues
    const message = error?.message || '';
    const errorName = error?.name || error?.constructor?.name || '';

    return (errorName === 'RequiredError' || message.includes('RequiredError')) &&
           (message.includes('Required parameter flow was null or undefined') ||
            message.includes('flow was null or undefined'));
  }

  private isExistingAccountError(error: any): boolean {
    // Enhanced logging to understand the actual error structure
    console.log('Checking for existing account error:', {
      error: error,
      status: error?.response?.status || error?.status,
      message: error?.message,
      responseData: error?.response?.data,
      responseDataError: error?.response?.data?.error,
      responseDataErrorMessage: error?.response?.data?.error?.message,
      responseDataUiMessages: error?.response?.data?.ui?.messages,
      fullResponse: error?.response
    });

    // Check for 400 status code
    const status = error?.response?.status || error?.status;
    if (status !== 400) {
      return false;
    }

    // Collect all possible error message sources
    const messageSources = [
      error?.message,
      error?.response?.data?.error?.message,
      error?.response?.data?.message,
      error?.response?.statusText,
      // Ory UI messages (common in Kratos responses)
      ...(error?.response?.data?.ui?.messages || []).map((msg: any) => msg?.text || ''),
      // Ory error details
      error?.response?.data?.error?.reason,
      error?.response?.data?.error?.details?.message,
      // Additional nested error paths
      error?.response?.data?.errors?.[0]?.message,
      error?.data?.error?.message,
      error?.data?.message
    ].filter(Boolean);

    const allMessages = messageSources.join(' ').toLowerCase();

    console.log('All error messages combined:', allMessages);

    // Enhanced pattern matching for account conflicts
    const accountExistsPatterns = [
      'already exists',
      'account exists',
      'email is already',
      'user already exists',
      'duplicate',
      'conflict',
      'email already registered',
      'email already taken',
      'identifier already exists',
      'identity already exists',
      // Ory Kratos specific patterns
      'registration.*exists',
      'email.*registered',
      'identity.*duplicate',
      'credential.*exists',
      // Common HTTP 409 conflict messages that might come as 400
      'resource already exists',
      'unique constraint',
      'violates unique constraint'
    ];

    const hasAccountConflict = accountExistsPatterns.some(pattern => {
      const regex = new RegExp(pattern, 'i');
      return regex.test(allMessages);
    });

    console.log('Account conflict detected:', hasAccountConflict);

    return hasAccountConflict;
  }

  private async handleFlowParameterError(error: any) {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('Authentication Error'),
      message: this.translate.instant('There was an issue with the authentication flow. Please try signing in with your email and password instead.'),
      buttons: [
        {
          text: this.translate.instant('Cancel'),
          role: 'cancel'
        },
        {
          text: this.translate.instant('Sign In with Email'),
          handler: () => {
            this.navigateToEmailSignIn();
          }
        }
      ],
    });
    await alert.present();
  }

  private async navigateToEmailSignIn() {
    try {
      await this.router.navigate([SIGN_IN]);
    } catch (error) {
      console.error('Navigation to sign-in failed:', error);
    }
  }

  private async handleExistingAccountError(error: any) {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('Account Already Exists'),
      message: this.translate.instant('An account with this email already exists. Please sign in with your email and password.'), // TODO ' first, then you can link your Google account in Account Settings.'),
      buttons: [
        {
          text: this.translate.instant('Cancel'),
          role: 'cancel'
        },
        {
          text: this.translate.instant('Sign In with Email'),
          handler: () => {
            this.navigateToEmailSignIn();
          }
        }
      ],
    });
    await alert.present();
  }
  private async signInWithOidcUsingLoginFlow(updateBody: {
    method: 'oidc';
    provider: string;
    id_token: string;
    /** Required by Kratos when the provider embeds a nonce in the ID token (common on iOS Google / Sign in with Apple). */
    id_token_nonce?: string;
  }) {
    try {
      // Create a login flow for OIDC
      const loginFlow = await this.frontendApi.createBrowserLoginFlow();

      // Submit to Ory using login flow
      const response = await this.frontendApi.updateLoginFlow({
        flow: loginFlow.data.id,
        updateLoginFlowBody: updateBody
      });

      console.log('Google sign-in with login flow successful:', response);
      await this.waitForSessionAndNavigate({ existingAccountSwitch: true });

      return true;
    } catch (error) {
      console.error('Login flow failed:', error);
      return false;
    }
  }

  /**
   * Ory Console often defines separate OIDC providers for “web/Android” vs “iOS” redirect URIs.
   * Registration on Android binds the identity to the web provider id; signing in on iOS must use
   * that same provider when submitting `id_token`, otherwise login fails and registration returns
   * “already exists”. On iOS we try web first, then the iOS-specific provider.
   */
  private nativeGoogleOryProviderCandidates(): string[] {
    if (Capacitor.getPlatform() === 'ios') {
      return [
        oryGoogleWebOidcRedirectPathPart,
        oryGoogleIosOidcRedirectPathPart,
      ];
    }
    return [oryGoogleWebOidcRedirectPathPart];
  }

  private async tryGoogleNativeLoginWithAllProviders(
    idToken: string,
    idTokenNonce: string | undefined,
  ): Promise<boolean> {
    for (const provider of this.nativeGoogleOryProviderCandidates()) {
      const ok = await this.signInWithOidcUsingLoginFlow({
        method: 'oidc',
        provider,
        id_token: idToken,
        id_token_nonce: idTokenNonce,
      });
      if (ok) {
        return true;
      }
    }
    return false;
  }

  async signInWithGoogle(): Promise<SocialAuthResult> {
    try {
      this.loadingService.setIsLoading(true);

      console.log('AuthService - Initializing Google Social Login.');
      if (Capacitor.getPlatform() === 'android') {
        await SocialLogin.initialize({
          google: {
            webClientId: googleWebClientId,
            mode: 'online',
            redirectUrl: googleRedirectUrl + oryGoogleWebOidcRedirectPathPart
          }
        });
      }
      if (Capacitor.getPlatform() === 'ios') {
        await SocialLogin.initialize({
          google: {
            iOSClientId: googleIosClientId,
            iOSServerClientId: googleWebClientId, // The same value as webClientId
            mode: 'online',
            redirectUrl: googleRedirectUrl + oryGoogleIosOidcRedirectPathPart
          }
        });
      }

      console.log('Auth Service - Google Social Login initialized');

      if (Capacitor.isNativePlatform()) {
        // Use Capgo Social Login for native platforms
        const googleLoginResponse = await SocialLogin.login({
          provider: 'google',
          options: {
            scopes: ['openid', 'profile', 'email']
          }
        });

        let idToken = '';
        let idTokenNonce: string | undefined;

        if ('idToken' in googleLoginResponse.result) {
          idToken = googleLoginResponse.result.idToken || '';
          console.log('AuthService - Google idToken: ', idToken);
          if (idToken) {
            const payload = this.decodeJWTPayload(idToken);
            idTokenNonce =
              typeof payload?.nonce === 'string' ? payload.nonce : undefined;
          }
        }

        if (!idToken) {
          throw new Error('No id token received from Google');
        }

        const loginSuccess = await this.tryGoogleNativeLoginWithAllProviders(
          idToken,
          idTokenNonce,
        );

        if (loginSuccess) {
          return { usedExistingAccount: true };
        }

        console.log('Login flow failed, trying registration flow...');

        let registrationError: unknown;
        for (const provider of this.nativeGoogleOryProviderCandidates()) {
          try {
            const registrationFlow =
              await this.frontendApi.createBrowserRegistrationFlow();

            await this.frontendApi.updateRegistrationFlow({
              flow: registrationFlow.data.id,
              updateRegistrationFlowBody: {
                method: 'oidc' as const,
                provider,
                id_token: idToken,
                id_token_nonce: idTokenNonce,
              },
            });

            console.log('Google sign-in successful:', provider);

            await this.waitForSessionAndNavigate();
            return { usedExistingAccount: false };
          } catch (err) {
            registrationError = err;
            console.error(
              `Google registration attempt failed for provider ${provider}:`,
              err,
            );
          }
        }

        if (
          registrationError &&
          this.isExistingAccountError(registrationError)
        ) {
          const recovered = await this.tryGoogleNativeLoginWithAllProviders(
            idToken,
            idTokenNonce,
          );
          if (recovered) {
            return { usedExistingAccount: true };
          }
        }

        throw registrationError ?? new Error('Google registration failed');

      } else {
        // Fallback to browser-based flow for web
        return this.signInWithGoogleBrowser();
      }
    } catch (error) {
      console.error('Google sign-in error: ', error);
      await this.handleError(error);
      return { usedExistingAccount: false };
    } finally {
      this.loadingService.setIsLoading(false);
    }
  }

  private async waitForSessionAndNavigate(
    options: { existingAccountSwitch?: boolean } = {},
    maxRetries = 30,
    delay = 1000
  ) {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const session = await this.getOrySession();
        if (session) {
          if (options.existingAccountSwitch) {
            await this.handleExistingAccountSwitch();
          } else {
            await this.updateSessionJWT({
              retryNativeOryWhoami: Capacitor.isNativePlatform(),
            });
          }
          await this.refreshSignedInVoiceCloneTrialState();
          await this.router.navigate([ACCOUNT_SETTINGS]);
          this.schedulePurchasesIdentifyAfterNavigation();
          return;
        }
      } catch (error) {
        console.log(`Session check attempt ${i + 1} failed`);
      }

      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    console.error('Failed to establish session after OAuth');
    await this.router.navigate([SIGN_UP]);
  }

  private async signInWithGoogleBrowser(): Promise<SocialAuthResult> {
    try {
      // Your existing browser-based implementation
      const loginFlow = await this.frontendApi.createBrowserLoginFlow({
        refresh: false,
      });

      const googleNode = loginFlow.data.ui.nodes.find(
        (node) => {
          if ('type' in node.attributes && 'name' in node.attributes && 'value' in node.attributes) {
            return node.attributes.type === 'submit' &&
              node.attributes.name === 'provider' &&
              node.attributes.value === oryGoogleWebOidcRedirectPathPart;
          }
          return false;
        }
      );

      if (!googleNode) {
        throw new Error('Google OAuth provider not configured');
      }

      const actionUrl = loginFlow.data.ui.action;
      const formData = new FormData();
      formData.append('provider', oryGoogleWebOidcRedirectPathPart);
      formData.append('flow', loginFlow.data.id);

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = actionUrl;

      formData.forEach((value, key) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value.toString();
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

      await this.waitForSessionAndNavigate({ existingAccountSwitch: true });
      return { usedExistingAccount: true };
    } catch (error) {
      console.error('Browser sign-in error:', error);
      await this.handleError(error);
      return { usedExistingAccount: false };
    }
  }

  async signInWithFacebook(): Promise<SocialAuthResult> {
    try {
      this.loadingService.setIsLoading(true);

      await SocialLogin.initialize({
        facebook: {
          appId: 'APP_ID', // TODO add this once generated
          clientToken: 'CLIENT_TOKEN', // TODO add this once generated
        }
      })

      if (Capacitor.isNativePlatform()) {
        // Use Capgo Social Login for native Facebook authentication
        const result = await SocialLogin.login({
          provider: 'facebook',
          options: {
            permissions: ['email', 'public_profile'],
            limitedLogin: false
          }
        });

        const accessToken = result.result.accessToken;

        if (!accessToken) {
          throw new Error('No access token received from Facebook');
        }

        const loginSuccess = await this.signInWithOidcUsingLoginFlow({
          method: 'oidc',
          provider: 'facebook',
          id_token: String(accessToken),
        });

        if (loginSuccess) {
          return { usedExistingAccount: true };
        }

        // Create a registration flow for OIDC
        const registrationFlow = await this.frontendApi.createBrowserRegistrationFlow();

        // Create the update body with the proper structure
        const updateBody = {
          method: 'oidc' as const,
          provider: 'facebook',
          id_token: String(accessToken), // Facebook uses access_token instead of id_token
        };

        // Submit to Ory
        const response = await this.frontendApi.updateRegistrationFlow({
          flow: registrationFlow.data.id,
          updateRegistrationFlowBody: updateBody
        });

        // Handle successful authentication
        console.log('Facebook sign-in successful:', response);

        // Update session and navigate
        await this.getOrySession();
        await this.router.navigate([ACCOUNT_SETTINGS]);
        this.schedulePurchasesIdentifyAfterNavigation();
        return { usedExistingAccount: false };

      } else {
        // Fallback to browser-based Facebook OAuth flow
        return this.signInWithFacebookBrowser();
      }

    } catch (error) {
      console.error('Facebook sign-in error:', error);
      await this.handleError(error);
      return { usedExistingAccount: false };
    } finally {
      this.loadingService.setIsLoading(false);
    }
  }

  private async signInWithFacebookBrowser(): Promise<SocialAuthResult> {
    try {
      // Initialize the browser login flow for Facebook OAuth
      const loginFlow = await this.frontendApi.createBrowserLoginFlow({
        refresh: false,
      });

      // Find the Facebook OAuth provider in the UI nodes
      const facebookNode = loginFlow.data.ui.nodes.find(
        (node) => {
          // Type guard to check if the node has input attributes
          if ('type' in node.attributes && 'name' in node.attributes && 'value' in node.attributes) {
            return node.attributes.type === 'submit' &&
              node.attributes.name === 'provider' &&
              node.attributes.value === oryFacebookOidcRedirectPathPart;
          }
          return false;
        }
      );

      if (!facebookNode) {
        throw new Error('Facebook OAuth provider not configured');
      }

      // Get the form action URL for Facebook OAuth
      const actionUrl = loginFlow.data.ui.action;

      // Create form data for Facebook OAuth submission
      const formData = new FormData();
      formData.append('provider', 'facebook');
      formData.append('flow', loginFlow.data.id);

      // Redirect to Facebook OAuth by submitting the form
      // In a web environment, this will redirect the browser to Facebook
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = actionUrl;

      // Add hidden inputs for the form data
      formData.forEach((value, key) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value.toString();
        form.appendChild(input);
      });

      // Submit the form to initiate Facebook OAuth flow
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

      await this.waitForSessionAndNavigate({ existingAccountSwitch: true });
      return { usedExistingAccount: true };
    } catch (error) {
      console.error('Facebook browser sign-in error:', error);
      await this.handleError(error);
      return { usedExistingAccount: false };
    }
  }

  async signInWithApple(): Promise<SocialAuthResult> {
    try {
      this.loadingService.setIsLoading(true);

      await SocialLogin.initialize({
        apple: {}
      });

      if (Capacitor.isNativePlatform()) {
        // Use Capgo Social Login for native Apple authentication
        const result = await SocialLogin.login({
          provider: 'apple',
          options: {
            scopes: ['email', 'name']
          }
        });

        this.debugAppleResponse(result);

        let idToken: string | null = null;
        let idTokenNonce: string | undefined;

        // Handle both online and offline response types
        if ('idToken' in result.result) {
          idToken = result.result.idToken || '';
          console.log('AuthService - Apple idToken: ', idToken);
          if (idToken) {
            const payload = this.decodeJWTPayload(idToken);
            idTokenNonce =
              typeof payload?.nonce === 'string' ? payload.nonce : undefined;
          }
        }

        if (!idToken) {
          throw new Error('No identity token received from Apple');
        }

        const loginSuccess = await this.signInWithOidcUsingLoginFlow({
          method: 'oidc',
          provider: oryAppleOidcRedirectPathPart,
          id_token: String(idToken),
          id_token_nonce: idTokenNonce,
        });

        if (loginSuccess) {
          return { usedExistingAccount: true };
        }

        // Create a registration flow for OIDC
        const registrationFlow = await this.frontendApi.createBrowserRegistrationFlow();

        // Create the update body with the proper structure
        const updateBody = {
          method: 'oidc' as const,
          provider: oryAppleOidcRedirectPathPart,
          id_token: String(idToken),
          id_token_nonce: idTokenNonce,
        };

        // Submit to Ory
        const response = await this.frontendApi.updateRegistrationFlow({
          flow: registrationFlow.data.id,
          updateRegistrationFlowBody: updateBody
        });

        // Handle successful authentication
        console.log('Apple sign-in successful:', response);

        await this.waitForSessionAndNavigate();
        return { usedExistingAccount: false };

      } else {
        // Fallback to browser-based Apple OAuth flow
        return this.signInWithAppleBrowser();
      }

    } catch (error) {
      console.error('Apple sign-in error:', error);
      await this.handleError(error);
      return { usedExistingAccount: false };
    } finally {
      this.loadingService.setIsLoading(false);
    }
  }

  private async signInWithAppleBrowser(): Promise<SocialAuthResult> {
    try {
      // Initialize the browser login flow for Apple OAuth
      const loginFlow = await this.frontendApi.createBrowserLoginFlow({
        refresh: false,
      });

      // Find the Apple OAuth provider in the UI nodes
      const appleNode = loginFlow.data.ui.nodes.find(
        (node) => {
          // Type guard to check if the node has input attributes
          if ('type' in node.attributes && 'name' in node.attributes && 'value' in node.attributes) {
            return node.attributes.type === 'submit' &&
              node.attributes.name === 'provider' &&
              node.attributes.value === oryAppleOidcRedirectPathPart;
          }
          return false;
        }
      );

      if (!appleNode) {
        throw new Error('Apple OAuth provider not configured');
      }

      // Get the form action URL for Apple OAuth
      const actionUrl = loginFlow.data.ui.action;

      // Create form data for Apple OAuth submission
      const formData = new FormData();
      formData.append('provider', oryAppleOidcRedirectPathPart);
      formData.append('flow', loginFlow.data.id);

      // Redirect to Apple OAuth by submitting the form
      // In a web environment, this will redirect the browser to Apple
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = actionUrl;

      // Add hidden inputs for the form data
      formData.forEach((value, key) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value.toString();
        form.appendChild(input);
      });

      // Submit the form to initiate Apple OAuth flow
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

      await this.waitForSessionAndNavigate({ existingAccountSwitch: true });
      return { usedExistingAccount: true };
    } catch (error) {
      console.error('Apple browser sign-in error:', error);
      await this.handleError(error);
      return { usedExistingAccount: false };
    }
  }

  private async handleExistingAccountSwitch(): Promise<void> {
    const jwtBeforeSwitch = await this.getSessionJwtToken();
    const hadAnonymousJwt =
      !!jwtBeforeSwitch && this.isAnonymousJwtPayload(jwtBeforeSwitch);
    const hasOrphanedLocalAnonymousData =
      hadAnonymousJwt && (await this.hasLocalAnonymousOwnedContent());

    await this.clearAnonymousSessionArtifacts();

    if (!hasOrphanedLocalAnonymousData) {
      await this.updateSessionJWT({ retryNativeOryWhoami: true });
      return;
    }

    this.loadingService.setIsLoading(false);

    const alert = await this.alertCtrl.create({
      header: 'Switching accounts',
      message:
        'This Storees account already exists. Anonymous profiles and voice clones saved on this device belong to a different account and will be removed from this device.',
      buttons: [this.translate.instant('general.ok')],
    });
    await alert.present();
    await alert.onDidDismiss();

    this.loadingService.setIsLoading(true);

    const profileService = this.injector.get(ProfileService);
    const dexieProfilesService = this.injector.get(DexieProfilesService);
    const dexieNarratorsService = this.injector.get(DexieNarratorsService);
    const dexieTrophiesService = this.injector.get(DexieTrophiesService);
    const voiceCloneService = this.injector.get(VoiceCloneService);
    const localProfiles = await this.dexieDb.getProfiles();
    const localProfileIds = new Set(
      localProfiles
        .map((profile) => profile.id)
        .filter((id): id is string => !!id && id !== NULL_UUID)
    );

    await profileService.deleteAllProfilesFromLocal();
    await Promise.all([
      ...Array.from(localProfileIds).map((profileId) =>
        dexieTrophiesService.clearProfileTrophyData(profileId)
      ),
      dexieTrophiesService.clearProfileTrophyData(NULL_UUID),
      dexieTrophiesService.clearProfileTrophyData('default'),
    ]);
    await voiceCloneService.clearLocalVoiceCloneState();
    await this.updateSessionJWT({ retryNativeOryWhoami: true });
    await this.syncPushTokenForCurrentSession();

    const jwtToken = await this.getSessionJwtToken();
    if (jwtToken) {
      try {
        await dexieProfilesService.syncProfiles(jwtToken);
      } catch (error) {
        console.error(
          'AuthService - Profile sync failed after existing account switch:',
          error
        );
      }

      try {
        await dexieNarratorsService.syncNarrators(jwtToken);
      } catch (error) {
        console.error(
          'AuthService - Narrator sync failed after existing account switch:',
          error
        );
      }
    }

    profileService.refreshProfiles();
  }

  private async syncPushTokenForCurrentSession(): Promise<void> {
    const jwtToken = await this.getSessionJwtToken();
    if (!jwtToken) {
      return;
    }

    try {
      const pushNotificationService = this.injector.get(PushNotificationService);
      await pushNotificationService.checkRefreshAndSyncPushToken(jwtToken);
    } catch (error) {
      console.error('AuthService - Push token sync failed after session change:', error);
    }
  }

  private async clearAnonymousSessionArtifacts(): Promise<void> {
    this.session.set(PENDING_TROPHY_UNLOCKS_KEY, '');
    this.session.set(VOICE_CLONE_RETURN_ROUTE, '');
    this.session.set(VOICE_CLONE_SHOW_SUBSCRIBE_NOW, undefined);
    this.session.set(ADD_NEW_FROM_CHARACTER, undefined);
    this.session.set(LAST_CREATED_PROFILE_ID, undefined);
    this.session.set(PROFILE_EDIT_ORIGINAL_BIRTHDATE_KEY, '');
    this.session.set(PROFILE_EDIT_ORIGINAL_NARRATOR_KEY, '');
    this.session.set(PROFILE_EDIT_ORIGINAL_IMAGE_STYLE_KEY, '');
    this.session.set(PROFILE_EDIT_ORIGINAL_PROFILE_IMAGE_KEY, '');

    this.injector.get(ProfileCreationStateService).resetAllParams();
    this.injector.get(StoryCreationStateService).resetAllParams();
  }

  private async hasLocalAnonymousData(): Promise<boolean> {
    const jwtToken = await this.getSessionJwtToken();
    if (!jwtToken || !this.isAnonymousJwtPayload(jwtToken)) {
      return false;
    }
    return this.hasLocalAnonymousOwnedContent();
  }

  /** Profiles or voice-clone state created under a local anonymous session (Dexie). */
  private async hasLocalAnonymousOwnedContent(): Promise<boolean> {
    const localProfiles = await this.dexieDb.getProfiles();
    if (localProfiles.some((profile) => profile.id !== NULL_UUID)) {
      return true;
    }
    const voiceCloneService = this.injector.get(VoiceCloneService);
    return voiceCloneService.hasAnyVoiceCreated();
  }

  /** True if JWT was issued for an anonymous Storees session (`anonymousId` in payload). Ory whoami may still be 401. */
  public isAnonymousJwtPayload(jwtToken: string): boolean {
    const payload = this.parseJwtPayload(jwtToken);
    return !!payload?.['anonymousId'];
  }

  private decodeJWTPayload(token: string): any {
    try {
      const payloadBase64 = token.split('.')[1];
      const payload = JSON.parse(atob(payloadBase64));

      // Print out each item in the JWT payload
      console.log('JWT Payload contents:');
      Object.keys(payload).forEach(key => {
        console.log(`${key}:`, payload[key]);
      });

      return payload;
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  // Add this helper method to your class
  private debugAppleResponse(result: any) {
    console.log('=== Apple Sign-In Debug Info ===');
    console.log('Full result object:', JSON.stringify(result, null, 2));

    if (result.result) {
      console.log('Result properties:', Object.keys(result.result));
      Object.keys(result.result).forEach(key => {
        console.log(`${key}:`, result.result[key]);
      });
    }

    console.log('=== End Debug Info ===');
  }
}
